import { TicketAvgPriority } from '@/modules/Ticket/types/response'
import {
    AlertTriangle,
    CheckCircleIcon,
    Clock,
    ClockIcon,
    TrendingDown,
} from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'

interface TicketTimeResponseChartProps {
    avg_resolution_time_by_priority?: TicketAvgPriority[]
}

const PRIORITY_CONFIG = {
    high: {
        icon: AlertTriangle,
        gradient: 'from-red-500 to-red-600',
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-600',
    },
    medium: {
        icon: Clock,
        gradient: 'from-yellow-500 to-orange-500',
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-600',
    },
    low: {
        icon: TrendingDown,
        gradient: 'from-green-500 to-emerald-500',
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-600',
    },
}

const TicketTimeResponseChart: React.FC<TicketTimeResponseChartProps> = ({
    avg_resolution_time_by_priority = [],
}) => {
    const { t } = useTranslation('ticket')

    const priorityData = useMemo(() => {
        const priorities = ['high', 'medium', 'low'] as const
        return priorities.map((priority) => {
            const found = avg_resolution_time_by_priority.find(
                (item) => item.ticket_priority === priority
            )
            return {
                priority,
                label: t(`ticket-${priority}`),
                hours: Math.floor(found?.avg_resolution_time || 0),
                total: found?.total_tickets || 0,
                resolved: found?.resolved_tickets || 0,
                pending: found?.pending_tickets || 0,
                config: PRIORITY_CONFIG[priority],
            }
        })
    }, [avg_resolution_time_by_priority, t])

    return (
        <div className="bg-white rounded-[8px] p-4 lg:col-span-4 col-span-12 half-col-print">
            <Typogaphy className="text-primary font-semibold capitalize">
                {t('average-resolution-time')} (
                {priorityData?.reduce((acc, item) => acc + item.total, 0)})
            </Typogaphy>

            <div className="mt-4 space-y-3">
                {priorityData.map((item) => {
                    const Icon = item.config.icon
                    return (
                        <div
                            key={item.priority}
                            className={`flex items-center gap-3 px-4 py-2 rounded-md border ${item.config.bg} ${item.config.border}`}
                        >
                            {/* Icon */}
                            <div
                                className={`w-8 h-8 rounded-md bg-gradient-to-br ${item.config.gradient} flex items-center justify-center shadow-sm`}
                            >
                                <Icon className="w-4 h-4 text-white" />
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium text-gray-700">
                                    {item.label}
                                </div>
                                <div className="flex items-center gap-2 text-xs mt-0.5">
                                    <span className="text-green-600 flex items-center gap-1">
                                        <CheckCircleIcon className="w-3 h-3 text-green-600" />{' '}
                                        {item.resolved}
                                    </span>
                                    <span className="text-orange-500 flex items-center gap-1">
                                        <ClockIcon className="w-3 h-3 text-yellow-600" />{' '}
                                        {item.pending}
                                    </span>
                                    <span className="text-gray-400">
                                        / {item.total}
                                    </span>
                                </div>
                            </div>

                            {/* Time */}
                            <div className="text-right">
                                <div
                                    className={`text-2xl font-bold ${item.total > 0 ? item.config.text : 'text-gray-300'}`}
                                >
                                    {item.total > 0 ? (
                                        <>
                                            {item.hours}
                                            <span className="text-sm font-normal ml-0.5">
                                                h
                                            </span>
                                        </>
                                    ) : (
                                        '-'
                                    )}
                                </div>
                                <div className="text-[10px] text-gray-400">
                                    {t('average')}
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default TicketTimeResponseChart
